/**
 * Snel nalopen — "Tinder" voor typeringen (Joris 19 sep 2026): één project per kaart, Enter = klopt,
 * velden aanpassen als het niet klopt, → volgende. Schrijft via de bestaande typering-check-routes:
 * /toepassen (Gripp vrije velden), /branches/{company_id} (branche van de klant), /beoordeeld (status).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, FormControl, FormControlLabel, InputLabel, LinearProgress,
  MenuItem, Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { buildGrippProjectUrl } from '../../utils/grippDeepLink';

const VELDEN = [['doel', 'Doel'], ['projecttype', 'Projecttype'], ['dienst', 'Dienst']];   // omvang volgt het bedrag
const euro = (v) => `€ ${Math.round(v || 0).toLocaleString('nl-NL')}`;

const DOELEN_GELDIG = ['Interne communicatie', 'Arbeidsmarkt communicatie', 'Corporate en publiekscommunicatie', 'Marketing en merkcontent'];

export default function SnelNalopen({ open, onClose, onGewijzigd, startFilter = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [alleenOpen, setAlleenOpen] = useState(true);
  const [alleenAI, setAlleenAI] = useState(false);
  const [alleenDoel, setAlleenDoel] = useState(false);   // alleen leeg of vervallen doel
  const [alleenLaag, setAlleenLaag] = useState(false);   // alleen AI-voorstellen met lage zekerheid
  const [eindklant, setEindklant] = useState('');
  const [idx, setIdx] = useState(0);
  const [velden, setVelden] = useState({});
  const [branche, setBranche] = useState('');
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setError(null); setIdx(0);
    setAlleenDoel(startFilter === 'doel');
    if (startFilter === 'doel') setAlleenOpen(false);
    authFetch(`${BACKEND_URL}/Api/gripp/typering-check/projecten?vanaf=2026-01-01`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!ok) throw new Error(d.error || d.detail || 'Laden mislukt'); setData(d); })
      .catch((e) => setError(e.message));
  }, [open]);

  const doelProbleem = (p) => !p.huidig?.doel || !DOELEN_GELDIG.includes(p.huidig.doel);
  const lijst = useMemo(() => (data?.projecten || []).filter((p) => (!alleenOpen || !p.beoordeeld) && (!alleenAI || p.voorstel) && (!alleenDoel || doelProbleem(p)) && (!alleenLaag || (p.voorstel && (p.voorstel.zekerheid || 0) < 0.5))), [data, alleenOpen, alleenAI, alleenDoel, alleenLaag]); // eslint-disable-line react-hooks/exhaustive-deps
  const huidig = lijst[idx] || null;

  useEffect(() => {
    if (!huidig) return;
    setVelden({ ...huidig.huidig });
    setBranche(huidig.branche || '');
    setEindklant(huidig.eindklant || '');
  }, [huidig]);

  const gewijzigd = huidig && (VELDEN.some(([k]) => (velden[k] || null) !== (huidig.huidig[k] || null)) || (branche || '') !== (huidig.branche || '') || (eindklant || '') !== (huidig.eindklant || ''));

  const markeer = useCallback((p, status) => {
    setData((prev) => prev ? { ...prev, projecten: prev.projecten.map((x) => (x.gripp_id === p.gripp_id ? { ...x, beoordeeld: { status, op: new Date().toISOString() } } : x)) } : prev);
  }, []);

  const volgende = useCallback(() => {
    // Na een beoordeling verdwijnt het item uit de 'alleen open'-lijst, dus dezelfde index toont het volgende.
    setIdx((i) => (alleenOpen ? Math.min(i, Math.max(0, lijst.length - 2)) : Math.min(i + 1, lijst.length - 1)));
  }, [alleenOpen, lijst.length]);

  const klopt = useCallback(async () => {
    if (!huidig || bezig) return;
    setBezig(true);
    try {
      await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/beoordeeld`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gripp_id: huidig.gripp_id, status: 'ok' }) });
      markeer(huidig, 'ok');
      volgende();
    } catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  }, [huidig, bezig, markeer, volgende]);

  const opslaan = useCallback(async () => {
    if (!huidig || bezig) return;
    setBezig(true);
    try {
      const veldWijz = Object.fromEntries(VELDEN.filter(([k]) => (velden[k] || null) !== (huidig.huidig[k] || null) && velden[k]).map(([k]) => [k, velden[k]]));
      if (Object.keys(veldWijz).length) {
        const r = await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/toepassen`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ gripp_id: huidig.gripp_id, velden: veldWijz }] }) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok || (d.fouten || []).length) throw new Error(d.error || d.detail || (d.fouten || [])[0] || 'Gripp bijwerken mislukt');
      }
      if ((branche || '') !== (huidig.branche || '') && branche && huidig.company_id) {
        const r = await authFetch(`${BACKEND_URL}/Api/gripp/branches/${huidig.company_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ branche, company_name: huidig.company }) });
        if (!r.ok) throw new Error('Branche opslaan mislukt');
      }
      if ((eindklant || '') !== (huidig.eindklant || '')) {
        const r = await authFetch(`${BACKEND_URL}/Api/gripp/portfolios/eindklant/${huidig.gripp_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eindklant }) });
        if (!r.ok) throw new Error('Eindklant opslaan mislukt');
      }
      await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/beoordeeld`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ gripp_id: huidig.gripp_id, status: 'aangepast' }) });
      setData((prev) => prev ? { ...prev, projecten: prev.projecten.map((x) => {
        if (x.gripp_id === huidig.gripp_id) return { ...x, huidig: { ...x.huidig, ...veldWijz }, branche: branche || x.branche, eindklant: eindklant || null, beoordeeld: { status: 'aangepast', op: new Date().toISOString() } };
        if (branche && x.company_id === huidig.company_id) return { ...x, branche };
        return x;
      }) } : prev);
      onGewijzigd?.({ gripp_id: huidig.gripp_id, velden: veldWijz, branche: branche || null, company_id: huidig.company_id, eindklant: eindklant || null });
      toast.success(`${huidig.number} bijgewerkt`);
      volgende();
    } catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  }, [huidig, bezig, velden, branche, eindklant, volgende, onGewijzigd]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'Enter') { e.preventDefault(); if (gewijzigd) opslaan(); else klopt(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setIdx((i) => Math.min(i + 1, lijst.length - 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, gewijzigd, opslaan, klopt, lijst.length]);

  const neemAI = () => {
    if (!huidig?.voorstel) return;
    setVelden((v) => ({ ...v, ...Object.fromEntries(huidig.voorstel.afwijkingen.map((a) => [a.veld, a.voorstel])) }));
  };

  const totaal = data?.totaal || 0;
  const klaar = data ? data.projecten.filter((p) => p.beoordeeld).length : 0;
  const url = huidig ? buildGrippProjectUrl(huidig.gripp_id, huidig.company_id) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 17 }}>⚡ Snel nalopen — projecten 2026</Typography>
              <Typography variant="caption" color="text.secondary">Enter = klopt (of opslaan als je iets wijzigde) · → overslaan · ← vorige. Wijzigingen gaan direct naar Gripp.</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={alleenOpen} onChange={(e) => { setAlleenOpen(e.target.checked); setIdx(0); }} />} label={<Typography variant="caption">alleen nog niet beoordeeld</Typography>} />
              <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={alleenAI} onChange={(e) => { setAlleenAI(e.target.checked); setIdx(0); }} />} label={<Typography variant="caption">alleen met AI-voorstel</Typography>} />
              <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={alleenDoel} onChange={(e) => { setAlleenDoel(e.target.checked); setIdx(0); }} />} label={<Typography variant="caption">alleen leeg/oud doel</Typography>} />
              <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={alleenLaag} onChange={(e) => { setAlleenLaag(e.target.checked); setIdx(0); }} />} label={<Typography variant="caption">alleen lage zekerheid (AI)</Typography>} />
              <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Sluiten</Button>
            </Stack>
          </Stack>
          {data && (
            <Box sx={{ mt: 1.25 }}>
              <LinearProgress variant="determinate" value={totaal ? (100 * klaar) / totaal : 0} sx={{ height: 6, borderRadius: 3 }} />
              <Typography variant="caption" color="text.secondary">{klaar} van {totaal} beoordeeld · {lijst.length} in deze lijst{huidig ? ` · nu ${idx + 1}/${lijst.length}` : ''}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
          {data && !huidig && <Alert severity="success">Alles in deze lijst is beoordeeld. Zet de schakelaar uit om ook beoordeelde projecten te zien.</Alert>}
          {huidig && (
            <Box>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.25 }}>
                    {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1e293b', textDecoration: 'none' }}>{huidig.number} — {huidig.name} ↗</a> : `${huidig.number} — ${huidig.name}`}
                  </Typography>
                  <Typography sx={{ color: '#475569', mt: 0.25 }}>
                    {huidig.company}{huidig.exclusive ? ` · Very Video (${huidig.exclusive})` : ''} · {euro(huidig.amount)} · start {huidig.start_date}{huidig.status_name ? ` · ${huidig.status_name}` : ''}
                  </Typography>
                </Box>
                {huidig.beoordeeld && <Chip size="small" label={`al beoordeeld: ${huidig.beoordeeld.status}`} sx={{ bgcolor: '#ecfdf5', color: '#15803d', fontWeight: 700 }} />}
              </Stack>
              {huidig.description && <Typography sx={{ fontSize: 13.5, color: '#334155', mb: 1 }}>{huidig.description}</Typography>}
              {huidig.regels?.length > 0 && (
                <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Offerteregels</Typography>
                  {huidig.regels.map((r, i) => <Typography key={i} sx={{ fontSize: 12.5, color: '#334155' }}>· {r}</Typography>)}
                </Box>
              )}
              {huidig.voorstel && (
                <Alert severity="info" sx={{ mb: 2 }} action={<Button size="small" onClick={neemAI} sx={{ textTransform: 'none', fontWeight: 700 }}>Neem over</Button>}>
                  <b>AI-voorstel</b> ({Math.round((huidig.voorstel.zekerheid || 0) * 100)}%): {huidig.voorstel.afwijkingen.map((a) => `${a.veld}: ${a.huidig || '—'} → ${a.voorstel}`).join(' · ')}
                  {huidig.voorstel.reden ? ` — ${huidig.voorstel.reden}` : ''}
                </Alert>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2.5 }}>
                {VELDEN.map(([k, label]) => (
                  <FormControl key={k} size="small" fullWidth>
                    <InputLabel>{label}</InputLabel>
                    <Select label={label} value={velden[k] || ''} onChange={(e) => setVelden((v) => ({ ...v, [k]: e.target.value }))}
                      sx={{ bgcolor: (velden[k] || null) !== (huidig.huidig[k] || null) ? '#fef3c7' : (velden[k] ? '#fff' : '#fff1f2') }}>
                      <MenuItem value=""><em>leeg</em></MenuItem>
                      {(data.opties[k] || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      {velden[k] && !(data.opties[k] || []).includes(velden[k]) && <MenuItem value={velden[k]}>{velden[k]} (oud)</MenuItem>}
                    </Select>
                  </FormControl>
                ))}
                <FormControl size="small" fullWidth disabled>
                  <InputLabel>Omvang (uit bedrag)</InputLabel>
                  <Select label="Omvang (uit bedrag)" value={huidig.omvang_berekend || ''}><MenuItem value={huidig.omvang_berekend || ''}>{huidig.omvang_berekend || '–'}</MenuItem></Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Branche klant</InputLabel>
                  <Select label="Branche klant" value={branche} onChange={(e) => setBranche(e.target.value)} sx={{ bgcolor: (branche || '') !== (huidig.branche || '') ? '#fef3c7' : '#fff' }}>
                    <MenuItem value=""><em>niet ingedeeld</em></MenuItem>
                    {(data.opties.branche || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
                {huidig.exclusive && (
                  <TextField size="small" fullWidth label="Eindklant (bureauwerk)" value={eindklant} onChange={(e) => setEindklant(e.target.value)}
                    sx={{ gridColumn: { md: 'span 2' }, '& .MuiInputBase-root': { bgcolor: (eindklant || '') !== (huidig.eindklant || '') ? '#fef3c7' : '#fff' } }} />
                )}
              </Box>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                <Button size="small" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(i - 1, 0))} sx={{ textTransform: 'none' }}>← Vorige</Button>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setIdx((i) => Math.min(i + 1, lijst.length - 1))} sx={{ textTransform: 'none' }}>Overslaan →</Button>
                  {gewijzigd ? (
                    <Button variant="contained" disableElevation disabled={bezig} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
                      {bezig ? 'Opslaan…' : 'Opslaan in Gripp & volgende (Enter)'}
                    </Button>
                  ) : (
                    <Button variant="contained" disableElevation disabled={bezig} onClick={klopt} sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#15803d', '&:hover': { bgcolor: '#166534' } }}>
                      ✓ Klopt (Enter)
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
